import { CaretRightIcon } from '@phosphor-icons/react'
import { formatMoney } from '@paybae/shared'
import { useNavigate } from 'react-router'

import { ProgressRing } from '@/components/Donut'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useJustificacion } from '@/lib/queries'

/**
 * Aviso de dinero sin clasificar, para el inicio (§12).
 *
 * Solo aparece cuando de verdad falta algo por ubicar. Un aviso que sale
 * siempre —aunque sea para decir «todo en orden»— deja de leerse a la tercera
 * vez, y entonces tampoco se lee el día que importa.
 *
 * Va en durazno, el tinte de «lo que sale»: lo que falta casi siempre es un
 * gasto que nadie capturó, y es el mismo color del encabezado de la pantalla a
 * la que lleva, así que el destino se reconoce antes de tocar. Solo el fondo:
 * ni texto coral ni icono de alarma, porque el dinero «perdido» casi nunca es
 * un problema, es un recibo sin registrar. El anillo repite en pequeño la
 * misma figura que se verá al entrar.
 *
 * Sin borde ni sombra, como toda caja del papel, y con la misma tipografía que
 * el aviso de ingreso: cuando salen los dos, uno debajo del otro, tienen que
 * leerse como dos avisos del mismo sistema y no como dos estilos.
 */
export function AvisoJustificar({ className }: { className?: string }) {
  const navigate = useNavigate()
  const { data } = useJustificacion()

  // Mientras carga o si falla no se dibuja nada: reservar el hueco de un aviso
  // que quizá no exista movería la pantalla entera al resolverse la consulta.
  if (!data || data.sinJustificarCents === 0) return null

  return (
    <button
      type="button"
      onClick={() => {
        haptic('light')
        navigate('/justificar')
      }}
      className={cn(
        'flex min-h-[44px] w-full items-center gap-3 rounded-hero bg-tint-peach p-4 text-left',
        'active:scale-[0.99]',
        className,
      )}
    >
      <ProgressRing percent={data.porcentajeIdentificado} size={44} stroke={5}>
        <span data-money className="text-[11px] font-bold text-ink">
          {Math.round(data.porcentajeIdentificado)}
        </span>
      </ProgressRing>

      <span className="min-w-0 flex-1">
        <span className="block font-display text-[16px] font-bold leading-snug tracking-[-0.02em] text-ink">
          Faltan{' '}
          <span data-money>{formatMoney(data.sinJustificarCents, { hideZeroCents: true })}</span> por
          clasificar
        </span>
        <span className="mt-0.5 block text-[13px] leading-snug text-ink-soft">
          Casi siempre es un gasto fijo sin registrar. Te decimos cuál.
        </span>
      </span>

      <CaretRightIcon size={18} weight="bold" className="shrink-0 text-ink-soft" />
    </button>
  )
}
